import React from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import Spinner from '@/components/platform/ui/Spinner';

interface SecondaryButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export default function SecondaryButton({
  children,
  onClick,
  type = 'button',
  disabled = false,
  loading = false,
  size = 'md',
  className = '',
}: SecondaryButtonProps) {
  const { darkMode } = useDarkMode();

  const baseClasses = darkMode
    ? 'bg-white/10 font-semibold text-white shadow-xs hover:bg-white/20 touch-manipulation focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 inline-flex items-center justify-center gap-2'
    : 'bg-white font-semibold text-gray-900 ring-1 shadow-xs ring-gray-300 ring-inset hover:bg-gray-100 touch-manipulation focus:outline-hidden focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 inline-flex items-center justify-center gap-2';

  const sizeClasses = {
    xs: 'rounded-md px-2 py-1.5 text-sm',
    sm: 'rounded-md px-2 py-1.5 text-sm',
    md: 'rounded-md px-3 py-2 text-sm',
    lg: 'rounded-md px-3 py-2 text-sm',
    xl: 'rounded-md px-3.5 py-2.5 text-sm',
  };

  const disabledClasses = 'disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${sizeClasses[size]} ${disabledClasses} ${className}`}
    >
      {loading ? (
        <>
          <Spinner size="sm" darkMode={darkMode} />
          <span>Loading...</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
